import React, { Fragment, useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import withStyles from "elevate-ui/withStyles";
import Input from "elevate-ui/Input";
import classNames from "classnames";
import Button from "elevate-ui/Button";
import Typography from "elevate-ui/Typography";

import OAuthGoogle from "../common/OAuthGoogle";
import OAuthFacebook from "../common/OAuthFacebook";

import { withGlobalContext } from "../common/GlobalContext";
import Captcha from "../common/Captcha";

type Props = {
  classes: Object,
  className: string,
  tenant: string,
};

const LoginForm = ({
  classes,
  className,
  tenant,
  location,
  assertCaptcha,
  assertCaptchaV2,
  isPageForceV2Captcha,
}: Props) => {
  const [token, setToken] = useState(null);
  useEffect(() => {
    if (isPageForceV2Captcha()) {
      assertCaptchaV2();
    }
  }, []);

  useEffect(
    () => {
      if (token) {
        // $FlowIgnore
        document.getElementById("login-form").submit();
      }
    },
    [token]
  );

  const onSubmit = () => {
    if (isPageForceV2Captcha()) {
      // $FlowIgnore
      document.getElementById("login-form").submit();
    } else {
      assertCaptcha("login").then((token) => {
        setToken(token);
      });
    }
  };

  return (
    <div className={classNames(classes.root, className)}>
      <Typography type="heading2" gutterBottom className={classes.heading}>
        Welcome Back!
      </Typography>
      <Typography type="body" className={classes.subHeading}>
        Log in to access your user profile, favorited listings, saved searches
        and more.
      </Typography>
      <Formik
        initialValues={{ email: "", password: "" }}
        validationSchema={() =>
          Yup.object().shape({
            email: Yup.string()
              .email("Invalid email address")
              .required("Email is required"),
            password: Yup.string().required("Password is required"),
          })
        }
        onSubmit={onSubmit}
        render={({ isSubmitting }) => (
          <Form method="POST" id="login-form" noValidate>
            <div className={classes.row}>
              <Field id="email" name="email" label="Email" component={Input} />
            </div>
            <div className={classes.row}>
              <Field
                type="password"
                id="password"
                name="password"
                label="Password"
                component={Input}
              />
            </div>
            <Captcha
              token={token}
              isPageForceV2Captcha={isPageForceV2Captcha()}
            />
            <Button
              type="submit"
              className={classes.loginBtn}
              disabled={isSubmitting}
            >
              <span className={classes.loginText}>Log in</span>
            </Button>
          </Form>
        )}
      />
      {tenant && tenant === "elevate" ? (
        <Fragment>
          <div className={classes.or}>
            <span className={classes.orSpan}> or </span>
          </div>
          <div
            className={classNames({
              [classes.socialLogin]: true,
              ref__sso: tenant && tenant === "elevate",
            })}
          >
            <OAuthGoogle href="/auth/google" title="Log in with Google" />
            <OAuthFacebook href="/auth/facebook" title="Log in with Facebook" />
          </div>
        </Fragment>
      ) : null}
      <div className={classes.closingContainer}>
        <div>
          Forgot your password?{" "}
          <span>
            <a className={classes.link} href={`/forgot`}>
              Reset it here.
            </a>
          </span>
        </div>
        <div>
          Don't have an account?{" "}
          <span>
            <a className={classes.link} href={`/signup${location.search}`}>
              Sign up here.
            </a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default withStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor: "#fff !important",
    },
  },
  root: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "400px",
    padding: "0px 12px",
    margin: "96px auto",
  },
  heading: {
    textAlign: "center",
    marginBottom: theme.spacing.unit * 0.75,
  },
  subHeading: {
    textAlign: "center",
    margin: "auto",
    marginBottom: theme.spacing.unit * 3,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    "& > * + *": {
      marginLeft: theme.spacing.unit * 0.75,
    },
  },
  seperator: {
    display: "flex",
    flexDirection: "row",
    margin: "20px auto",
  },
  or: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "600",
    fontSize: "14px",
    color: theme.colors.gray["600"],

    "&:after, &:before": {
      content: "''",
      display: "block",
      background: theme.colors.gray["300"],
      width: "200px",
      height: "2px",
      margin: "20px 0",
    },
  },
  orSpan: {
    padding: "0 8px",
  },
  socialLogin: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    "& > * + *": {
      marginLeft: theme.spacing.unit * 0.75,
    },
  },
  closingContainer: {
    display: "flex",
    flexDirection: "column",
    color: theme.colors.gray["600"],
    fontWeight: "600",
    fontSize: "18px",
    marginTop: theme.spacing.unit * 4,
    margin: "0 auto",

    "& > * + *": {
      marginTop: theme.spacing.unit,
    },
  },
  loginBtn: {
    width: "100%",
    height: "54px",
    margin: "16px 0",
    fontSize: "24px",
    fontWeight: "600",
  },
  loginText: {
    fontSize: "20px",
  },
  link: {
    color: `${theme.colors.primary["500"]} !important`,
    textDecoration: "underline",

    "&:hover": {
      textDecoration: "none !important",
    },
  },
}))(withGlobalContext(LoginForm));
